import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Leaf, BookOpen, AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// packageRates will be fetched dynamically

const comfortMultipliers = {
  ashram: { name: 'Ashram Simplicity', mult: 1.0, details: 'Organic vegetarian meals, shared Vedic rooms, active Sewa service.' },
  heritage: { name: 'Heritage Comfort', mult: 2.2, details: 'Restored royal Havelis, private yoga guides, comfortable travel.' },
  royal: { name: 'Royal Wellness', mult: 4.8, details: 'Ultra-exclusive palace resorts, helicopter transfers, VIP temple entrances.' }
};

const phoneConstraints = {
  '+91': { min: 10, max: 10 },
  '+1': { min: 10, max: 10 },
  '+44': { min: 10, max: 11 },
  '+61': { min: 9, max: 9 },
  '+971': { min: 9, max: 9 },
  '+49': { min: 10, max: 11 },
  '+33': { min: 9, max: 9 },
  '+39': { min: 9, max: 10 },
  '+34': { min: 9, max: 9 },
  '+65': { min: 8, max: 8 },
  '+60': { min: 9, max: 10 },
  '+81': { min: 10, max: 10 },
  '+86': { min: 11, max: 11 },
  '+27': { min: 9, max: 9 },
  '+64': { min: 8, max: 10 },
  '+55': { min: 10, max: 11 },
  '+52': { min: 10, max: 10 },
  '+31': { min: 9, max: 9 },
  '+41': { min: 9, max: 9 },
  '+46': { min: 7, max: 9 },
  '+972': { min: 8, max: 9 },
};

export default function RetreatBooking({ customItinerary, onResetCustomItinerary }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: localStorage.getItem('user_name') || localStorage.getItem('admin_name') || '',
    email: localStorage.getItem('user_email') || localStorage.getItem('admin_email') || '',
    phone: localStorage.getItem('user_phone') || localStorage.getItem('admin_phone') || '',
    packageName: '',
    comfort: 'heritage',
    duration: 7,
    guests: 1,
    startDate: '',
    intention: ''
  });
  const [countryCode, setCountryCode] = useState('+91');

  const [priceDetails, setPriceDetails] = useState({
    base: 0,
    multiplier: 1.0,
    total: 0
  });

  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingRef, setBookingRef] = useState('');

  // Dynamic Retreats State
  const [packageRates, setPackageRates] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch retreats on mount
  useEffect(() => {
    const fetchRetreats = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const res = await fetch(`${apiBase}/api/retreats`);
        if (res.status === 429) { 
          console.warn('Rate limit exceeded. Please wait a minute.');
          return;
        }

        if (res.ok) {
          const data = await res.json();
          const ratesObj = {};
          data.forEach(r => {
            ratesObj[r.id.toString()] = {
              name: r.name,
              baseRate: 150,
              desc: r.desc,
              image: r.image,
              fullDesc: r.fullDesc,
              highlights: r.highlights
            };
          });
          setPackageRates(ratesObj);
          
          // Set default package name dynamically if not already set or invalid
          setFormData(prev => {
            if (!ratesObj[prev.packageName] && data.length > 0) {
              return { ...prev, packageName: data[0].id.toString() };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Failed to fetch retreats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRetreats();
  }, []);


  // UI Flow States
  const [isBookingFlow, setIsBookingFlow] = useState(false);
  const [selectedPackageForDetails, setSelectedPackageForDetails] = useState(null);

  useEffect(() => {
    if (selectedPackageForDetails) {
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    } else {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    }
    return () => {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    };
  }, [selectedPackageForDetails]);

  // If a custom itinerary is passed down from the JourneyPlanner, pre-fill the form and jump to booking
  useEffect(() => {
    if (customItinerary) {
      const mappedPackage = customItinerary.focusObj.id === 'yoga' ? 'yoga' 
                          : customItinerary.focusObj.id === 'meditation' ? 'silent'
                          : customItinerary.focusObj.id === 'ayurveda' ? 'ayurveda'
                          : 'temple';
      
      const mappedComfort = customItinerary.styleObj.id === 'ashram' ? 'ashram'
                          : customItinerary.styleObj.id === 'royal' ? 'royal'
                          : 'heritage';

      setFormData(prev => ({
        ...prev,
        packageName: mappedPackage,
        comfort: mappedComfort,
        duration: customItinerary.days.length
      }));
      setIsBookingFlow(true);
    }
  }, [customItinerary]);

  // Recalculate price whenever selections change
  useEffect(() => {
    const pkg = packageRates[formData.packageName];
    if (!pkg) return;
    const comfortObj = comfortMultipliers[formData.comfort];
    
    const baseCost = pkg.baseRate * formData.duration;
    const comfortMultiplier = comfortObj.mult;
    const guestMultiplier = 1 + (formData.guests - 1) * 0.7; // 70% extra per guest
    
    const total = Math.round(baseCost * comfortMultiplier * guestMultiplier);

    setPriceDetails({
      base: baseCost,
      multiplier: comfortMultiplier,
      total
    });
  }, [formData.packageName, formData.comfort, formData.duration, formData.guests, packageRates]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'name') {
      value = value.replace(/[^A-Za-z\s]/g, '');
    }
    if (name === 'phone') {
      value = value.replace(/\D/g, '');
      const maxLen = phoneConstraints[countryCode]?.max || 15;
      if (value.length > maxLen) {
        return;
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };



  const handleSelectComfort = (comfortId) => {
    setFormData(prev => ({ ...prev, comfort: comfortId }));
  };

  const handleProceedToBooking = (pkgId) => {
    setFormData(prev => ({ ...prev, packageName: pkgId }));
    setSelectedPackageForDetails(null);
    setIsBookingFlow(true);
    window.scrollTo({ top: document.getElementById('booking').offsetTop, behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.startDate) {
      alert('Please fill out all required fields.');
      return;
    }
    
    // Call FastAPI backend database
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${apiBase}/api/bookings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: `${countryCode} ${formData.phone}`,
        packageName: formData.packageName,
        comfort: formData.comfort,
        duration: parseInt(formData.duration),
        guests: parseInt(formData.guests),
        startDate: formData.startDate,
        intention: formData.intention
      })
    })
      .then(res => {
        if (!res.ok) throw new Error("Database booking registration failed");
        return res.json();
      })
      .then(data => {
        setBookingRef(data.booking_ref);
        // Update final price calculated by backend source-of-truth engine
        setPriceDetails(prev => ({ ...prev, total: data.price }));
        setBookingConfirmed(true);
      })
      .catch(err => {
        console.log("Database offline. Running booking reservation in offline mode: ", err);
        // Local simulation fallback
        const ref = 'BHR-' + Math.random().toString(36).substring(2, 9).toUpperCase();
        setBookingRef(ref + " (Offline Mode)");
        setBookingConfirmed(true);
      });
  };

  const handleReset = () => {
    setBookingConfirmed(false);
    setIsBookingFlow(false);
    setFormData({
      name: localStorage.getItem('user_name') || localStorage.getItem('admin_name') || '',
      email: localStorage.getItem('user_email') || localStorage.getItem('admin_email') || '',
      phone: localStorage.getItem('user_phone') || localStorage.getItem('admin_phone') || '',
      packageName: '',
      comfort: 'heritage',
      duration: 7,
      guests: 1,
      startDate: '',
      intention: ''
    });
    if (onResetCustomItinerary) onResetCustomItinerary();
  };

  return (
    <section 
      id="booking" 
      style={{
        padding: '100px 0',
        backgroundColor: 'var(--bg-deep)',
        position: 'relative'
      }}
    >
      <div 
        style={{
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <span 
            className="text-saffron" 
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}
          >
            {t('booking.header')}
          </span>
          <h2 
            className="font-serif" 
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              marginTop: '10px',
              color: 'var(--text-light)'
            }}
          >
            {t('booking.title')}
          </h2>
          <div 
            style={{
              width: '60px',
              height: '2px',
              backgroundColor: 'var(--primary-saffron)',
              margin: '20px auto 0'
            }}
          />
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '100px 0', color: 'var(--text-muted)' }}>
            <div className="animate-spin" style={{ display: 'inline-block', width: '40px', height: '40px', border: '3px solid rgba(181,137,37,0.3)', borderTopColor: 'var(--primary-gold)', borderRadius: '50%', marginBottom: '20px' }} />
            <p style={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.9rem' }}>{t('booking.loading')}</p>
          </div>
        ) : (
          <>
            {/* The rest of the content will go here, but I need to make sure I wrap the existing content in a fragment */}

        {!isBookingFlow ? (
          /* Browsing Phase: Frame Grid */
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' }}>
              {Object.entries(packageRates).map(([id, pkg]) => (
                <div 
                  key={id}
                  className="glass-card hover-lift retreat-frame"
                  onClick={() => setSelectedPackageForDetails(id)}
                  style={{ cursor: 'pointer', overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}
                >
                  <div style={{ height: '260px', backgroundImage: `url(${pkg.image})`, backgroundSize: 'cover', backgroundPosition: 'center', transition: 'transform 0.5s ease' }} className="retreat-image-bg" />
                  <div style={{ padding: '25px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <h3 className="font-serif text-saffron" style={{ fontSize: '1.4rem', lineHeight: '1.2' }}>{t(`booking.retreats.${id}.name`, { defaultValue: pkg.name })}</h3>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '25px', flexGrow: 1 }}>{t(`booking.retreats.${id}.desc`, { defaultValue: pkg.desc })}</p>
                    <button className="btn-outline" style={{ width: '100%', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                      {t('booking.btnDetails')} <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Modal for Details */}
            {selectedPackageForDetails && createPortal(
              <div 
                className="animate-fade-in"
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                  backgroundColor: 'rgba(10, 10, 10, 0.85)', zIndex: 9999,
                  display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'
                }}
                onClick={() => setSelectedPackageForDetails(null)}
              >
                <div 
                  className="glass-card" 
                  data-lenis-prevent
                  style={{ maxWidth: '900px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 0, position: 'relative' }}
                  onClick={e => e.stopPropagation()}
                >
                  <button 
                    onClick={() => setSelectedPackageForDetails(null)}
                    style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, transition: 'var(--transition-fast)' }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(217, 119, 54, 0.8)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                  >
                    <X size={20} />
                  </button>
                  
                  {/* Detailed Content */}
                  <div style={{ height: '350px', backgroundImage: `url(${packageRates[selectedPackageForDetails].image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                  <div style={{ padding: '40px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
                      <h2 className="font-serif text-gradient" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}>{t(`booking.retreats.${selectedPackageForDetails}.name`, { defaultValue: packageRates[selectedPackageForDetails].name })}</h2>
                    </div>
                    <p style={{ color: 'var(--text-light)', fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '35px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {t(`booking.retreats.${selectedPackageForDetails}.fullDesc`, { defaultValue: packageRates[selectedPackageForDetails].fullDesc })}
                    </p>
                    <h4 className="font-serif text-saffron" style={{ fontSize: '1.3rem', marginBottom: '20px', borderBottom: '1px solid rgba(217,119,54,0.2)', paddingBottom: '10px' }}>{t('booking.modalHighlights')}</h4>
                    <ul style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px', marginBottom: '45px', listStyle: 'none', padding: 0 }}>
                      {packageRates[selectedPackageForDetails].highlights.map((h, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--text-muted)', fontSize: '1.05rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          <CheckCircle2 size={18} className="text-gold" style={{ flexShrink: 0 }} /> {t(`booking.retreats.${selectedPackageForDetails}.highlights_${i}`, { defaultValue: h })}
                        </li>
                      ))}
                    </ul>
                    
                    <button 
                      className="btn-gold pulse-gold-button"
                      style={{ width: '100%', padding: '20px', fontSize: '1.15rem', borderRadius: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}
                      onClick={() => handleProceedToBooking(selectedPackageForDetails)}
                    >
                      {t('booking.btnProceed')} <ArrowRight size={22} />
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          </>
        ) : (
          /* Booking Flow: Existing Split Layout */
          <>
            {!bookingConfirmed && (
              <button 
                onClick={() => setIsBookingFlow(false)}
                className="btn-outline"
                style={{ marginBottom: '30px', padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: '10px', borderRadius: '30px' }}
              >
                <ArrowLeft size={16} /> {t('booking.btnBack')}
              </button>
            )}

            {!bookingConfirmed ? (
              <div 
                className="booking-split animate-fade-in"
                style={{
                  display: 'flex',
                  gap: '40px'
                }}
              >
                {/* Left: Input Selection */}
                <div style={{ flex: '1.4' }}>
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                    

                    {/* 1. Choose Comfort Level */}
                    <div className="glass-card" style={{ padding: '30px' }}>
                      <h3 className="font-serif text-gold" style={{ fontSize: '1.3rem', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Leaf size={20} /> {t('booking.step1')}
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {Object.entries(comfortMultipliers).map(([id, comfort]) => (
                          <div 
                            key={id}
                            onClick={() => handleSelectComfort(id)}
                            style={{
                              padding: '16px 20px',
                              borderRadius: '6px',
                              border: '1px solid',
                              borderColor: formData.comfort === id ? 'var(--primary-gold)' : 'rgba(212, 175, 55, 0.1)',
                              backgroundColor: formData.comfort === id ? 'var(--primary-gold-trans)' : 'rgba(255, 255, 255, 0.45)',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'var(--transition-fast)'
                            }}
                          >
                            <div>
                              <h4 style={{ fontSize: '1rem', color: formData.comfort === id ? 'var(--primary-gold)' : 'var(--text-light)', fontWeight: '600' }}>
                                {t(`booking.comfort.${id}.name`, { defaultValue: comfort.name })} <span style={{ fontSize: '0.75rem', color: 'var(--primary-saffron)', marginLeft: '10px' }}>({comfort.mult}x tier)</span>
                              </h4>
                              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>{t(`booking.comfort.${id}.details`, { defaultValue: comfort.details })}</p>
                            </div>
                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid var(--primary-gold)', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center' }}>
                              {formData.comfort === id && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--primary-gold)' }} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 2. Duration, Guests & Date */}
                    <div className="glass-card" style={{ padding: '30px' }}>
                      <h3 className="font-serif text-gold" style={{ fontSize: '1.3rem', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <BookOpen size={20} /> {t('booking.step2')}
                      </h3>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
                        {/* Duration input */}
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('booking.labelDuration')}</label>
                          <input 
                            type="number" 
                            name="duration" 
                            min="3" 
                            max="60" 
                            value={formData.duration} 
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none'
                            }}
                          />
                        </div>

                        {/* Guest input */}
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('booking.labelGuests')}</label>
                          <input 
                            type="number" 
                            name="guests" 
                            min="1"
                            max="50"
                            value={formData.guests} 
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none'
                            }}
                          />
                        </div>

                        {/* Date input */}
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('booking.labelDate')}</label>
                          <input 
                            type="date" 
                            name="startDate" 
                            required
                            value={formData.startDate} 
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '10px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 3. Personal details */}
                    <div className="glass-card" style={{ padding: '30px' }}>
                      <h3 className="font-serif text-gold" style={{ fontSize: '1.3rem', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <User size={20} /> {t('booking.step3')}
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                          <input 
                            type="text" 
                            name="name" 
                            placeholder={t('booking.phName')} 
                            required
                            value={formData.name} 
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none'
                            }}
                          />
                          <input 
                            type="email" 
                            name="email" 
                            placeholder={t('booking.phEmail')} 
                            required
                            value={formData.email} 
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <select 
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none',
                              width: '100px'
                            }}
                          >
                            <option value="+91">+91 (IN)</option>
                            <option value="+1">+1 (US/CA)</option>
                            <option value="+44">+44 (UK)</option>
                            <option value="+61">+61 (AU)</option>
                            <option value="+971">+971 (AE)</option>
                            <option value="+49">+49 (DE)</option>
                            <option value="+33">+33 (FR)</option>
                            <option value="+39">+39 (IT)</option>
                            <option value="+34">+34 (ES)</option>
                            <option value="+65">+65 (SG)</option>
                            <option value="+60">+60 (MY)</option>
                            <option value="+81">+81 (JP)</option>
                            <option value="+86">+86 (CN)</option>
                            <option value="+27">+27 (ZA)</option>
                            <option value="+64">+64 (NZ)</option>
                            <option value="+55">+55 (BR)</option>
                            <option value="+52">+52 (MX)</option>
                            <option value="+31">+31 (NL)</option>
                            <option value="+41">+41 (CH)</option>
                            <option value="+46">+46 (SE)</option>
                            <option value="+972">+972 (IL)</option>
                          </select>
                          <input 
                            type="tel" 
                            name="phone" 
                            required
                            minLength={phoneConstraints[countryCode]?.min || 7}
                            maxLength={phoneConstraints[countryCode]?.max || 15}
                            placeholder={t('booking.phPhone')} 
                            value={formData.phone} 
                            onChange={handleInputChange}
                            style={{
                              flex: 1,
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid rgba(181, 137, 37, 0.25)',
                              borderRadius: '6px',
                              color: 'var(--text-light)',
                              outline: 'none'
                            }}
                          />
                        </div>
                        
                        <textarea 
                          name="intention" 
                          placeholder={t('booking.phIntention')} 
                          value={formData.intention} 
                          onChange={handleInputChange}
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.65)',
                            border: '1px solid rgba(181, 137, 37, 0.25)',
                            borderRadius: '6px',
                            color: 'var(--text-light)',
                            outline: 'none',
                            resize: 'none'
                          }}
                        />
                      </div>
                    </div>

                    {/* Submit button */}
                    <button 
                      type="submit" 
                      className="btn-gold pulse-gold-button"
                      style={{
                        padding: '16px',
                        borderRadius: '30px',
                        fontSize: '0.9rem',
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        cursor: 'pointer'
                      }}
                    >
                      {t('booking.btnConfirm')}
                    </button>
                  </form>
                </div>

                {/* Right: Summary Invoice Calculator */}
                <div style={{ flex: '1' }}>
                  <div 
                    className="glass-card" 
                    style={{ 
                      padding: '30px', 
                      position: 'sticky', 
                      top: '100px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '20px',
                      borderColor: 'var(--primary-saffron)'
                    }}
                  >
                    <h3 className="font-serif text-saffron" style={{ fontSize: '1.4rem', borderBottom: '1px solid rgba(217, 119, 54, 0.2)', paddingBottom: '12px' }}>
                      {t('booking.invoiceHeader')}
                    </h3>

                    {/* Selected Summary */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invPractice')}</span>
                        <span style={{ color: 'var(--text-light)', fontWeight: '600' }}>{t(`booking.retreats.${formData.packageName}.name`, { defaultValue: packageRates[formData.packageName].name })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invComfort')}</span>
                        <span style={{ color: 'var(--text-light)' }}>{t(`booking.comfort.${formData.comfort}.name`, { defaultValue: comfortMultipliers[formData.comfort].name })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invDuration')}</span>
                        <span style={{ color: 'var(--text-light)' }}>{formData.duration} {t('booking.invDays')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invSeekers')}</span>
                        <span style={{ color: 'var(--text-light)' }}>{formData.guests} {t('booking.invPersons')}</span>
                      </div>
                    </div>

                    {/* Calculator Lines */}
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invDailyRate')}</span>
                        <span>${packageRates[formData.packageName].baseRate} {t('booking.invPerDay')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invSubtotal')}</span>
                        <span>${priceDetails.base}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{t('booking.invLuxMultiplier')}</span>
                        <span>{priceDetails.multiplier}x</span>
                      </div>
                      {formData.guests > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--text-muted)' }}>{t('booking.invSeekerMultiplier', { guests: formData.guests })}</span>
                          <span>{(1 + (formData.guests - 1) * 0.7).toFixed(1)}x</span>
                        </div>
                      )}
                    </div>

                    {/* Final Cost */}
                    <div 
                      style={{ 
                        borderTop: '2px solid var(--primary-gold)', 
                        paddingTop: '20px', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                      }}
                    >
                      <span className="font-serif text-gold" style={{ fontSize: '1.25rem' }}>{t('booking.invTotal')}</span>
                      <span className="font-serif text-gradient" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                        ${priceDetails.total.toLocaleString()}
                      </span>
                    </div>

                    {/* Info Note */}
                    <div style={{ display: 'flex', gap: '10px', backgroundColor: 'rgba(217, 119, 54, 0.08)', padding: '12px', borderRadius: '6px', border: '1px solid rgba(217,119,54,0.15)', fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                      <AlertCircle size={16} className="text-saffron" style={{ flexShrink: 0 }} />
                      <span>{t('booking.invNote')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* High-Fidelity Success Receipt Ticket */
              <div className="animate-fade-in" style={{ animationDuration: '0.6s', maxWidth: '500px', margin: '0 auto' }}>
                <div 
                  className="glass-card animate-shine" 
                  style={{ 
                    border: '2px solid var(--primary-gold)', 
                    borderRadius: '16px', 
                    overflow: 'hidden',
                    background: 'linear-gradient(to bottom, #fcf9f2 0%, #faf5ea 100%)'
                  }}
                >
                  {/* Ticket Top */}
                  <div 
                    style={{ 
                      backgroundColor: 'rgba(181, 137, 37, 0.08)', 
                      padding: '20px', 
                      borderBottom: '1px dashed rgba(181, 137, 37, 0.35)',
                      textAlign: 'center',
                      position: 'relative'
                    }}
                  >
                    {/* Decorative side ticket cuts */}
                    <div style={{ position: 'absolute', bottom: '-10px', left: '-10px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'var(--bg-deep)' }} />
                    <div style={{ position: 'absolute', bottom: '-10px', right: '-10px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'var(--bg-deep)' }} />

                    <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--primary-gold)', letterSpacing: '0.3em' }}>{t('booking.ticketConfirmed')}</span>
                    <h3 className="font-serif text-gradient" style={{ fontSize: '1.8rem', marginTop: '10px' }}>{t('booking.ticketPassage')}</h3>
                    <span className="font-serif text-saffron" style={{ display: 'block', fontSize: '1rem', marginTop: '5px', letterSpacing: '0.05em' }}>
                      {t('booking.ticketRef')} {bookingRef}
                    </span>
                  </div>

                  {/* Ticket Details */}
                  <div style={{ padding: '25px 25px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px 20px', marginBottom: '25px', fontSize: '0.85rem' }}>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktName')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>{formData.name}</span>
                      </div>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktEmail')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--text-light)' }}>{formData.email}</span>
                      </div>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktPhone')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--text-light)' }}>{formData.phone || 'N/A'}</span>
                      </div>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktPackage')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--primary-gold)', fontWeight: '600' }}>{t(`booking.retreats.${formData.packageName}.name`, { defaultValue: packageRates[formData.packageName].name })}</span>
                      </div>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktStyle')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--text-light)' }}>{t(`booking.comfort.${formData.comfort}.name`, { defaultValue: comfortMultipliers[formData.comfort].name })}</span>
                      </div>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktDuration')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--text-light)' }}>{formData.duration} {t('booking.invDays')}</span>
                      </div>
                      <div>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('booking.tktDate')}</span>
                        <span style={{ fontSize: '1rem', color: 'var(--primary-saffron)' }}>{formData.startDate}</span>
                      </div>
                    </div>

                    {formData.intention && (
                      <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: 'rgba(181, 137, 37, 0.05)', borderRadius: '6px', border: '1px solid rgba(181, 137, 37, 0.1)' }}>
                        <span style={{ display: 'block', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem', marginBottom: '5px' }}>{t('booking.tktIntention')}</span>
                        <p style={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'var(--text-light)' }}>"{formData.intention}"</p>
                      </div>
                    )}

                    {/* Preparational Guide */}
                    <div style={{ borderTop: '1px solid rgba(212, 175, 55, 0.15)', paddingTop: '15px', fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <h4 style={{ color: 'var(--primary-gold)', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.85rem', marginBottom: '5px' }}>{t('booking.tktGuide')}</h4>
                      <p>{t('booking.guide1')}</p>
                      <p>{t('booking.guide2')}</p>
                      <p>{t('booking.guide3')}</p>
                    </div>
                  </div>

                  {/* Ticket Footer */}
                  <div 
                    style={{ 
                      backgroundColor: 'rgba(41, 33, 26, 0.05)', 
                      padding: '16px 20px', 
                      borderTop: '1px solid rgba(181, 137, 37, 0.2)',
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('booking.tktValue')}</span>
                      <p className="font-serif text-gold" style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>${priceDetails.total.toLocaleString()}</p>
                    </div>
                    <button 
                      onClick={handleReset}
                      className="btn-gold"
                      style={{
                        padding: '10px 20px',
                        borderRadius: '30px',
                        fontSize: '0.8rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {t('booking.btnBookAnother')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>

      <style>{`
        .retreat-frame {
          background: rgba(255, 255, 255, 0.45) !important;
          border: 1px solid rgba(181, 137, 37, 0.15) !important;
          border-radius: 12px;
          transition: all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) !important;
        }
        .retreat-frame:hover {
          transform: translateY(-8px);
          border-color: rgba(212, 175, 55, 0.5) !important;
          box-shadow: 0 20px 40px rgba(181, 137, 37, 0.1) !important;
        }
        .retreat-frame:hover .retreat-image-bg {
          transform: scale(1.06);
        }
        .booking-card {
          background: rgba(255, 255, 255, 0.8) !important;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(181, 137, 37, 0.2) !important;
          border-radius: 16px;
        }
        .booking-ticket {
          background: rgba(255, 255, 255, 0.9) !important;
          border: 1px dashed var(--primary-gold-border) !important;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
        }
        input, select, textarea {
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }
        input:focus, select:focus, textarea:focus {
          border-color: var(--primary-gold) !important;
          box-shadow: 0 0 0 3px rgba(181, 137, 37, 0.15), 0 4px 10px rgba(181, 137, 37, 0.05) !important;
          background: #ffffff !important;
        }
        @media (max-width: 768px) {
          #booking {
            padding: 60px 0 40px !important;
          }
          /* Retreat card grid: 1 col */
          #booking > div > div[style*='grid'] {
            grid-template-columns: 1fr !important;
          }
          /* Booking form / summary split */
          .booking-split {
            flex-direction: column !important;
            gap: 24px !important;
          }
          /* Retreat image height */
          .retreat-image-bg {
            height: 200px !important;
          }
          /* Booking ticket receipt */
          .booking-ticket {
            padding: 20px 16px !important;
          }
          /* Ticket detail grid → 1 col */
          .booking-ticket [style*='grid-template-columns: repeat(2'] {
            grid-template-columns: 1fr !important;
          }
          /* Modal detail panel */
          .retreat-modal {
            padding: 20px 16px !important;
          }
          .retreat-modal img {
            height: 200px !important;
          }
        }
      `}</style>
    </section>
  );
}
